// Localização: frontend/src/components/Financeiro/CardSaldoContas.jsx
// Card de Saldo Total das Contas Bancárias

import React, { useState } from 'react';
import Card from '../UI/Card';
import CardContent from '../UI/CardContent';
import Modal from '../UI/Modal';
import { Wallet, ChevronRight, Building2 } from 'lucide-react';

/**
 * @component CardSaldoContas
 * @description Card que exibe o saldo total das contas bancárias com possibilidade de ver detalhes
 * @param {number} saldoTotal - Saldo total de todas as contas
 * @param {Array} contas - Array de contas bancárias com seus saldos
 */
const CardSaldoContas = ({ saldoTotal = 0, contas = [] }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Garantir que saldoTotal seja sempre um número
    const saldoTotalNumero = parseFloat(saldoTotal) || 0;
    
    // Garantir que contas seja sempre um array
    const contasArray = Array.isArray(contas) ? contas : [];

    const formatCurrency = (value) => {
        const numValue = parseFloat(value) || 0;
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(numValue);
    };

    const getTipoContaLabel = (tipo) => {
        const tipos = {
            'Corrente': 'Conta Corrente',
            'Poupança': 'Poupança',
            'Poupanca': 'Poupança', // Compatibilidade com versões antigas
            'Investimento': 'Investimento',
            'Outro': 'Outro'
        };
        return tipos[tipo] || tipo;
    };

    return (
        <>
            <Card 
                className="transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer border-2 border-blue-200 hover:border-blue-400 bg-gradient-to-br from-blue-50 to-white shadow-md h-full flex flex-col overflow-hidden"
                onClick={() => setIsModalOpen(true)}
            >
                <CardContent className="pt-4 pb-4 px-3 sm:pt-6 sm:pb-5 sm:px-5 h-full flex flex-col">
                    {/* Cabeçalho: Título e Badge à esquerda, Ícone à direita */}
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide">
                                Saldo Total das Contas
                            </p>
                            <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                saldoTotalNumero >= 0 
                                    ? 'bg-emerald-100 text-emerald-700' 
                                    : 'bg-red-100 text-red-700'
                            }`}>
                                {saldoTotalNumero >= 0 ? 'Positivo' : 'Negativo'}
                            </div>
                        </div>
                        <div className={`p-2 sm:p-3 rounded-xl shadow-sm flex-shrink-0 ${
                            saldoTotalNumero >= 0 ? 'bg-blue-100' : 'bg-red-100'
                        }`}>
                            <Wallet className={`h-5 w-5 sm:h-6 sm:w-6 ${
                                saldoTotalNumero >= 0 ? 'text-blue-600' : 'text-red-600'
                            }`} />
                        </div>
                    </div>

                    {/* Conteúdo central - ocupa espaço disponível */}
                    <div className="flex-1 flex flex-col justify-end">
                        {/* Valor do saldo na parte inferior */}
                        <div className="mt-auto">
                            <p className={`text-xl sm:text-3xl font-bold mb-3 whitespace-nowrap overflow-hidden text-ellipsis ${
                                saldoTotalNumero >= 0 ? 'text-blue-600' : 'text-red-600'
                            }`}>
                                {formatCurrency(saldoTotalNumero)}
                            </p>
                            
                            {/* Informações na parte inferior */}
                            <div className="flex items-center justify-between gap-2 pt-3 border-t border-gray-200">
                                <p className="text-xs text-gray-600">
                                    <span className="font-semibold">{contasArray.length}</span> {contasArray.length === 1 ? 'conta ativa' : 'contas ativas'}
                                </p>
                                <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Modal com Detalhes das Contas */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Detalhes das Contas Bancárias"
                size="lg"
            >
                <div className="space-y-6">
                    {contasArray.length > 0 ? (
                        <>
                            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between">
                                    <span className="text-base font-semibold text-gray-700">
                                        Saldo Total
                                    </span>
                                    <span className={`text-2xl font-bold ${
                                        saldoTotalNumero >= 0 ? 'text-blue-600' : 'text-red-600'
                                    }`}>
                                        {formatCurrency(saldoTotalNumero)}
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                                {contasArray.map((conta) => (
                                    <div
                                        key={conta.id}
                                        className="border border-gray-200 rounded-lg p-6 hover:bg-gray-50 transition-colors shadow-sm"
                                    >
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1 pr-4">
                                                <div className="flex items-center gap-3 mb-3">
                                                    <Building2 className="h-6 w-6 text-gray-400" />
                                                    <h4 className="font-semibold text-gray-900 text-lg">
                                                        {conta.nome}
                                                    </h4>
                                                </div>
                                                <div className="space-y-2 text-sm text-gray-600 ml-9">
                                                    <p>
                                                        <span className="font-medium">Banco:</span> {conta.banco || 'Não informado'}
                                                    </p>
                                                    <p>
                                                        <span className="font-medium">Tipo:</span> {getTipoContaLabel(conta.tipo_conta)}
                                                    </p>
                                                    {conta.agencia && (
                                                        <p>
                                                            <span className="font-medium">Agência:</span> {conta.agencia}
                                                        </p>
                                                    )}
                                                    {conta.conta && (
                                                        <p>
                                                            <span className="font-medium">Conta:</span> {conta.conta}
                                                        </p>
                                                    )}
                                                    {conta.observacoes && (
                                                        <p className="mt-2 pt-2 border-t border-gray-200">
                                                            <span className="font-medium">Observações:</span> {conta.observacoes}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="mb-2">
                                                    <p className="text-xs text-gray-500 mb-1">Saldo Atual</p>
                                                    <p className={`text-xl font-bold ${
                                                        (conta.saldo_atual || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                        {formatCurrency(conta.saldo_atual || 0)}
                                                    </p>
                                                </div>
                                                {conta.saldo_inicial !== undefined && conta.saldo_inicial !== null && (
                                                    <div>
                                                        <p className="text-xs text-gray-500 mb-1">Saldo Inicial</p>
                                                        <p className="text-sm text-gray-600">
                                                            {formatCurrency(conta.saldo_inicial || 0)}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-12">
                            <Wallet className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500 text-lg">
                                Nenhuma conta bancária ativa encontrada
                            </p>
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default CardSaldoContas;

